import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface WebsitesAccordionProps {
  control: Control<UserFormData>;
  disabled?: boolean;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
}

export default function WebsitesAccordion({
  control,
  errors,
  disabled,
  watch,
}: WebsitesAccordionProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'websites',
  });

  const menuItems = [
    { value: 'app_install_page', label: 'App Install Page' },
    { value: 'blog', label: 'Blog' },
    { value: 'custom', label: 'Custom' },
    { value: 'ftp', label: 'FTP' },
    { value: 'home', label: 'Home' },
    { value: 'home_page', label: 'Home Page' },
    { value: 'other', label: 'Other' },
    { value: 'profile', label: 'Profile' },
    { value: 'reservations', label: 'Reservations' },
    { value: 'resume', label: 'Resume' },
    { value: 'work', label: 'Work' },
  ];

  const websitesErrors = errors.websites;
  const websitesCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({
      type: '',
      primary: false,
      customType: '',
      url: '',
    });
  };

  return (
    <SharedProfileInformationAccordion
      title="Websites"
      count={websitesCount}
      hasError={!!websitesErrors}
      onAdd={handleOnAdd}
      disabled={disabled}
    >
      {fields.map((item, index) => {
        const typeField = watch(`websites.${index}.type`);

        return (
          <Box key={item.id}>
            <Stack
              gap={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ my: 1.5 }}
            >
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`websites.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      inputProps={{
                        id: `websites-type-${index}`,
                      }}
                      InputLabelProps={{
                        htmlFor: `websites-type-${index}`,
                      }}
                      name="websites"
                      select
                      disabled={disabled}
                      label="Type"
                      error={!!websitesErrors?.[index]?.type}
                      helperText={
                        websitesErrors?.[index]?.type
                          ? 'This field is required.'
                          : ''
                      }
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {menuItems.map((item) => (
                        <MenuItem value={item.value} key={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                {typeField === 'custom' && (
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`websites.${index}.customType`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `websites-customType-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `websites-customType-${index}`,
                        }}
                        disabled={disabled}
                        name="websites"
                        label="Custom Type"
                        error={!!websitesErrors?.[index]?.customType}
                        helperText={
                          websitesErrors?.[index]?.customType
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                )}
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`websites.${index}.url`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      name="websites"
                      inputRef={field.ref}
                      error={!!websitesErrors?.[index]?.url}
                      helperText={
                        websitesErrors?.[index]?.url
                          ? 'This field is required.'
                          : ''
                      }
                      disabled={disabled}
                      autoComplete="off"
                      label="Url"
                      sx={{
                        minWidth: 225,
                      }}
                    />
                  )}
                />
              </Stack>

              <SharedProfileInformationItemActions
                index={index}
                totalCount={websitesCount}
                disabled={disabled}
                onMoveDown={() => move(index, index + 1)}
                onMoveUp={() => move(index, index - 1)}
                onDelete={() => remove(index)}
              />
            </Stack>
            {index !== fields.length - 1 && <Divider />}
          </Box>
        );
      })}
    </SharedProfileInformationAccordion>
  );
}
